import React, { forwardRef, useEffect } from 'react'
import { variantProvider } from '@/app'
import { APIClient } from '@/services'
import { CommunityFeed } from '@/types'
import { useCallback } from 'react'
import { FeedItem } from './FeedItem'
import { useFeedScroll } from './feedScroll'
import { View, ActionIcon, Text, ActivityIndicator } from '@/components'
import { useIsMobile } from '@/utils'
import { FeedDimensions } from './FeedItems/shared'
import { FeedCommentDrawer } from './CommentList'
import { SearchButton } from './atoms/SearchButton'
import { FilterButton } from './atoms/FilterButton'
import { motion } from 'framer-motion'

type FeedListProps = ReturnType<typeof APIClient.Community.useCommunity>['listProps']

const Parent = forwardRef<HTMLDivElement, React.PropsWithChildren>((props, ref) => {
  const isMobile = useIsMobile()
  const drawerVisible = FeedCommentDrawer.useIsVisible()

  return <motion.div
    ref={ref}
    {...props}
    css={styles.feed}
    transition={{ duration: 0.3 }}
    animate={{ width: (drawerVisible && !isMobile) ? '75vw' : '100vw' }}
  />
})

export const FeedList = (props: FeedListProps) => {
  const { data, isLoading } = props

  const {
    goToNext,
    goToPrevious,
    virtualizer,
    parentRef,
  } = useFeedScroll(props)

  const isEmpty = (data?.length <= 0 || !data) && !isLoading

  useEffect(() => {
    if (isEmpty) FeedCommentDrawer.close()
  }, [isEmpty])

  const RenderItem = useCallback(({ item, index }: { item: CommunityFeed; index: number }) => {
    return (
      <FeedItem
        item={item}
        index={index}
        goToNext={goToNext}
      />
    )
  }, [])

  return <>
    <Parent ref={parentRef}>
      {isLoading ? null : (
        <div css={[styles.itemsWrapper, { height: `${virtualizer.getTotalSize()}px` }]}>
          {virtualizer.getVirtualItems()?.map((virtualItem) => {
            const item = data?.[virtualItem.index]

            if (!item) return null

            return (
              <div
                key={virtualItem.key}
                data-index={virtualItem.index}
                ref={virtualizer.measureElement}
                style={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  width: '100%',
                  transform: `translateY(${virtualItem.start}px)`,
                  scrollSnapAlign: 'center',
                }}
              >
                <RenderItem
                  index={virtualItem.index}
                  item={item}
                />
              </div>
            )
          })}
        </div>
      )}

      {isEmpty || isLoading ? (
        <View variants={['column', 'gap:1', 'center', 'textCenter', 'fullWidth', 'fullHeight']}>
          {isLoading ? (
            <View variants={['bg:neutral1', 'padding:1', 'center', 'border-radius:rounded']}>
              <ActivityIndicator debugName='feed:loading' />
            </View>
          ) : <>
            <Text text='Not found!' variants={['h4', 'color:neutral1']} />
            <Text text='We cannot find any items at the moment.' variants={['p1', 'color:neutral1']} />
          </>}
        </View>
      ) : null}
    </Parent>

    <FilterButton />
    <SearchButton />

    <FeedCommentDrawer.Container>
      <View style={styles.actions}>
        <ActionIcon
          variants={['feedAction', 'iconSize:4']}
          debugName=''
          name='arrow-up'
          onPress={goToPrevious}
        />
        <ActionIcon
          variants={['feedAction', 'iconSize:4']}
          debugName=''
          name='arrow-down'
          onPress={goToNext}
        />
      </View>

      <FeedCommentDrawer />
    </FeedCommentDrawer.Container>
  </>
}

const styles = variantProvider.createComponentStyle(theme => ({
  feed: {
    width: '100vw',
    height: 'calc(100dvh - (104px))',
    overflowX: 'hidden',
    scrollbarWidth: 'none',
    '-ms-overflow-style': 'none',
    scrollSnapType: 'y mandatory',
    overflowY: 'scroll',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'flex-start',

    [theme.media.down(FeedDimensions.mobileBreakpoint)]: {
      padding: 0,
    },
  },
  itemsWrapper: {
    position: 'relative',
    width: FeedDimensions.WIDTH,

    [theme.media.down(FeedDimensions.mobileBreakpoint)]: {
      width: '100%'
    },
  },
  actions: {
    ...theme.presets.column,
    ...theme.spacing.gap(1.2),
    ...theme.spacing.paddingRight(3),
    [theme.media.down('small')]: {
      display: 'none',
    }
  },
  header: {
    ...theme.presets.absolute,
    top: theme.spacing.value(2),
    right: theme.spacing.value(3),
    left: theme.spacing.value(3),
    ...theme.presets.row,
    ...theme.presets.justifySpaceBetween,
    ...theme.presets.alignCenter,
    ...theme.spacing.gap(1),
  },
}), true)
